/**
 * <load-more> — appends the next page of products into the existing grid.
 *
 * Design notes that are load-bearing, not preference:
 *
 * THE RESET MECHANISM. facets.js dispatches nothing after it re-renders (its only
 * dispatchEvent fires BEFORE the swap), so there is no event to listen to. Instead this
 * element is rendered INSIDE #ProductGridContainer, which facets.js replaces wholesale.
 * That makes element lifecycle the signal: disconnectedCallback means "the grid was
 * replaced, abandon everything", connectedCallback means "here is fresh server truth".
 * All state is re-read from data attributes; nothing is kept at module scope. A
 * MutationObserver was rejected because it cannot tell facets.js's innerHTML swap apart
 * from our own append.
 *
 * NO HISTORY WRITES. An earlier design rewrote the URL to ?page=N. It was cut: it
 * destroys the filter query params, produces a link that server-renders page N alone with
 * no way back to page 1, moves the sessionStorage key mid-session, and fires the GTM
 * History Change trigger, inflating pageviews into Google Ads and Criteo.
 *
 * COMING BACK. The record is keyed on pathname plus an ALLOWLIST of result-shaping params
 * (filter.*, sort_by, q) and written on pagehide, not on click. Both of those are scars:
 * keying on the whole query string meant Shopify's own preview params and any campaign's
 * utm_* changed the key so the record was never found, and saving on click meant the
 * remembered spot was the button rather than the shoe. Scroll restoration is left on the
 * browser's 'auto' on purpose; see the note in onClick for why taking it over was worse
 * than the flash it avoided.
 *
 * NATIVE FETCH. Captured at parse time because a third-party script replaces window.fetch
 * on load. This file is loaded with defer, which runs before window.load.
 *
 * PAGINATION IS THE FALLBACK. The real <a href="?page=N"> anchors ship visible from Liquid
 * and are hidden here with the `hidden` attribute (removed from the tab order, not merely
 * clipped). Every failure path reveals them again, so the shopper can always page manually.
 */
(function () {
  'use strict';

  const nativeFetch = window.fetch.bind(window);

  /**
   * Master switch for the COLD rebuild: refetching the pages a shopper had when the browser
   * did not keep the document. ON.
   *
   * It does not govern the warm path. When the page comes back from the browser's cache the
   * document is already intact and there is nothing to rebuild, and the only thing needed
   * there is onPageShow re-asserting the offset, which runs regardless of this switch.
   *
   * The snippet has the matching switch and both must agree: if the snippet armed while this
   * did not, the grid would be hidden waiting for a rebuild that never comes.
   */
  const RESTORE_ENABLED = true;

  const KEY_PREFIX = 'wsLoadMore:';
  const RECORD_TTL_MS = 30 * 60 * 1000;
  /**
   * Ceiling on cards in the grid, not on clicks.
   *
   * The old ceiling was 5 pages, and it was there because every <quick-buy> registered its
   * own document-level activeSibling listener, so choosing a colour ran the handler once per
   * card on the page. That is fixed in global.js and that reason is gone.
   *
   * A ceiling still has to exist, for a different and larger reason. Measured page counts of
   * ACTIVE published products, at 36 a page: כל המוצרים 85 pages, נעליים לנשים 30,
   * נעלי בית 20, תיקים ומזוודות 19, OUTLET 16, HOKA 13. Uncapped, a patient shopper on
   * כל המוצרים appends 3,060 cards and about 156,000 nodes into one document, and the phone
   * does not survive it.
   *
   * Measured cost of a full forced layout on a desktop, on the real card (51 nodes, 1.6
   * images): 36 cards 25ms, 180 138ms, 540 354ms, 1080 733ms. Linear, and a mid-range phone
   * pays four to six times that. It matters here specifically because setListHeight() in
   * QuickBuy reads heights, so every colour swatch click forces a layout, and choosing a
   * colour is the most common action in a shoe shop.
   *
   * 720 is twenty pages. It lets נעלי בית, תיקים, OUTLET and HOKA finish completely, and
   * stops only the two collections nobody browses to the end of. Counted in CARDS because
   * cards are what cost memory, reflow and scroll frames; pages are just how they arrive.
   * On reaching it the real pagination comes back, so the next page is still one click away
   * and nothing dead-ends.
   */
  const MAX_CARDS = 720;

  // Restoration counts pages, not cards, and only as a runaway guard: it stops as soon as
  // the product the shopper was looking at is back on the page. A shopper who left from
  // page 9 costs nine fetches, one who left from page 2 costs two, and nobody pays for the
  // pages that sat below where they were standing.
  const RESTORE_HARD_CAP = 20;

  // How many page requests restoration may have in flight at once. Every one is a full
  // section render on Shopify's side, so this is deliberately not unbounded, but it is high
  // enough that the journeys people actually make finish in a single wave.
  const RESTORE_CONCURRENCY = 8;
  const EMPTY_PAGE_CHAIN = 3;   // a valid page can render zero cards; chain past it
  const INSERT_BATCH = 12;

  /**
   * Wait a frame, but never wait forever.
   *
   * requestAnimationFrame does not fire while a tab is hidden, and the insert loop awaits it
   * between batches. A shopper who switches apps mid-load, or any background tab, left that
   * loop parked after the first batch of 12: absorb never returned, pagesLoaded never
   * incremented, and everything downstream that keys off pagesLoaded silently stopped,
   * including saving the position at all. Observed live: 48 cards in the grid and
   * pagesLoaded still 1.
   */
  const raf = () =>
    new Promise((r) => {
      let done = false;
      const fin = () => { if (!done) { done = true; r(); } };
      requestAnimationFrame(fin);
      setTimeout(fin, 250);
    });

  /**
   * Storage key: pathname plus ONLY the params that change which products are shown.
   *
   * This is an allowlist, and it has to be. The first version stripped `page` and
   * `section_id` and kept everything else, which meant any incidental param changed the
   * key and the record could not be found on the way back. Shopify's own theme preview
   * appends _ab, _fd, _sc and preview_theme_id; real traffic arrives carrying utm_source,
   * gclid, fbclid. None of those change the result set, and a blocklist can never be
   * complete. Only filters, sort and the search term are allowed to key a record.
   */
  const RESULT_PARAM = /^(filter\.|sort_by$|q$)/;

  function computeKey() {
    const kept = [...new URLSearchParams(location.search).entries()]
      .filter(([k]) => RESULT_PARAM.test(k))
      .sort((a, b) => (a[0] + a[1] < b[0] + b[1] ? -1 : 1));
    return KEY_PREFIX + location.pathname + '|' + new URLSearchParams(kept).toString();
  }

  /**
   * The key is computed ONCE per element, at connect, and then frozen for that element's life.
   *
   * Recomputing it per call looked harmless and was not. facets.js pushStates the new filter
   * URL BEFORE it swaps the grid, so during that window location.search already describes the
   * incoming result set while the DOM still holds the outgoing one. Any write in that window
   * files the old grid's position under the new filter's key, and the shopper who later
   * back-navigates into that filter is rebuilt to a position from a different result set.
   *
   * Freezing costs nothing, because an element's lifetime already IS one grid's lifetime:
   * facets.js destroys and re-creates this element on every filter, sort and clear-all.
   */
  function readRecord(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const rec = JSON.parse(raw);
      if (!rec || typeof rec.pages !== 'number') return null;
      if (Date.now() - (rec.ts || 0) > RECORD_TTL_MS) return null;
      return rec;
    } catch (_) {
      return null;
    }
  }

  function writeRecord(key, rec) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ ...rec, ts: Date.now() }));
    } catch (_) {
      /* private mode, quota — never fatal */
    }
  }

  function clearRecord(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (_) {}
  }

  /**
   * True when this DOCUMENT was loaded by a back/forward traversal that missed bfcache.
   *
   * Read once, at module scope, and consumed once. Both halves matter. It is a fact about the
   * document load: PerformanceNavigationTiming is created when the document is created and is
   * never added to or mutated by a same-document navigation, so the value stays true for as
   * long as the page is open. connectedCallback, by contrast, runs again for every facets
   * re-render. Left as a live check, the first filter a shopper applied after arriving by the
   * back button would silently trigger a full rebuild and yank the page to an old scroll
   * position. Consuming the latch means restoration can be attempted on the first connect of
   * this document and never again.
   */
  /**
   * Only the first connect of a document is a page load. Every later one is facets.js having
   * replaced #ProductGridContainer, because that is the only other thing that creates this
   * element. That makes "not the first connect" an exact signal for "the shopper just
   * filtered, sorted or cleared", which the theme otherwise gives us no way to detect:
   * facets.js dispatches nothing after the swap.
   */
  let documentFirstConnect = true;

  let coldArrivalUnconsumed = (() => {
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      // back/forward ONLY. Reload was included for a while, because a refresh drops the
      // appended pages just as completely and the shopper reasonably expects to keep their
      // place. It was reverted: mobile got materially worse the moment it went in, and the
      // mechanism fits. iOS Safari discards backgrounded tabs and reloads them by itself, so
      // 'reload' fires far more often on a phone than on a desktop, and every one of those
      // armed the hiding and expected a rebuild. Any rebuild that then bailed revealed the
      // grid at the top. More arming meant more of exactly the failure being reported.
      // Reload counts as well as back/forward, because a refresh drops the appended pages
      // just as completely and the shopper expects to keep their place either way.
      //
      // It was briefly taken out on the theory that it was what made mobile worse. That was
      // wrong: the restore has since been switched off entirely and mobile still loses the
      // shopper's place, so the cause is elsewhere. Kept in, at the shopper's request, on the
      // evidence that the refresh case itself behaved well while it was there.
      return !!nav && (nav.type === 'back_forward' || nav.type === 'reload');
    } catch (_) {
      return false;
    }
  })();

  function takeColdArrival() {
    const was = coldArrivalUnconsumed;
    coldArrivalUnconsumed = false;
    return was;
  }

  /**
   * Reveal the grid again after a rebuild.
   *
   * The class is put on <html> by the inline script in snippets/load-more.liquid, which runs
   * during parsing because the browser's own scroll restoration cannot be pre-empted any
   * later than that. Taking it off is this file's job, and it must happen on every path,
   * including the ones where restoration decided to do nothing at all. The snippet also arms
   * an 8 second timeout for the case where this file never runs.
   */
  function endRestoringPaint() {
    try {
      document.documentElement.classList.remove('ws-restoring');
    } catch (_) {}
    // And hand the scroll back. This half matters more than the hiding.
    //
    // scrollRestoration is a property of the SESSION HISTORY ENTRY, not of the document, so
    // 'manual' left behind outlives the page that set it. The failure that creates is silent
    // and lands on the path we most want to work: a later visit to the same entry that DOES
    // hit bfcache would have its scroll suppressed by a flag set during an earlier cold
    // rebuild, and nothing would put it back, because a bfcache restore never re-runs
    // connectedCallback. The shopper lands at the top of the collection with no way to tell
    // why. Every exit therefore returns the entry to 'auto'.
    try {
      history.scrollRestoration = 'auto';
    } catch (_) {}
  }

  class LoadMore extends HTMLElement {
    connectedCallback() {
      // Unconditional heal. Earlier versions of this component set scrollRestoration to
      // 'manual' and could leak it into the tab, where it survives every later navigation and
      // silently suppresses the browser's own restore. A shopper mid-session on the old code
      // would otherwise stay broken until they closed the tab. Cheap, idempotent, and 'auto'
      // is the default we now rely on everywhere.
      try {
        history.scrollRestoration = 'auto';
      } catch (_) {}

      // FIRST, before any early return below. A filter that narrows the results to a single
      // page takes the `!this.nextUrl` exit a few lines down, and that is the case where the
      // shopper is most stranded: the grid is now short and their old offset lands on the
      // footer. Consuming the flag late meant exactly that case never scrolled.
      const isFacetsRerender = !documentFirstConnect;
      documentFirstConnect = false;
      if (isFacetsRerender) this.scrollToResults();

      this.button = this.querySelector('button');
      if (!this.button) {
        endRestoringPaint();
        return;
      }

      this.controller = null;
      this.busy = false;
      this.pagesLoaded = 1;
      this.lastAnchor = null;
      this.lastScrollY = 0;
      this.anchorTimer = null;
      this.key = computeKey(); // frozen for this element's life; see computeKey
      this.progress = this.querySelector('.load-more__progress');
      this.progressCount = this.querySelector('.load-more__count');
      this.progressFill = this.querySelector('.load-more__fill');
      this.totalItems = parseInt(this.dataset.totalItems, 10) || 0;
      this.servedPage = parseInt(this.dataset.currentPage, 10) || 1;

      // Only take over when there is in fact a next page to fetch.
      //
      // Liquid renders this component under {% if paginate.pages > 1 %}, which is a property
      // of the whole result set and not of the page being served. Arriving straight on the
      // last page (a bookmark, a crawler, or the shopper following the pagination we reveal
      // when the card ceiling is reached) therefore rendered a button with nothing to load
      // AND hid the pagination behind it: a dead end with no way back to page 1.
      if (!this.nextUrl) {
        this.hidden = true;
        this.togglePagination(false);
        endRestoringPaint();
        return;
      }

      this.hidden = false;
      this.togglePagination(true);
      this.updateProgress();

      this.button.addEventListener('click', this.onClick);

      // A cold back-navigation is the only case where we rebuild without being asked, and
      // only ever onto page 1, which is the only page restoration knows how to replay onto.
      if (this.servedPage !== 1) {
        // Served deep, e.g. a shopper following the pagination we reveal at the card ceiling,
        // or a Google result for page 7. `page` is deliberately not part of the key, so this
        // document shares a key with page 1 while describing a completely different slice.
        // Anything stored under it is now misleading in both directions.
        clearRecord(this.key);
        endRestoringPaint();
      } else if (RESTORE_ENABLED && takeColdArrival()) {
        this.maybeRestore().finally(() => endRestoringPaint());
      } else {
        // Arrived at page 1 by an ordinary navigation. Any record here belongs to an earlier
        // visit, or was inherited from the tab that opened this one, because sessionStorage
        // is COPIED into a tab opened from a link. Left in place it would lie in wait: the
        // shopper's first back-navigation inside this new tab would rebuild pages they never
        // loaded and teleport them into a position that was never theirs.
        clearRecord(this.key);
        // Also the facets path: a filter re-render after a back-navigation lands here with
        // the latch already spent, and the hiding class still on <html> from the original
        // load. Without this the whole filtered grid would stay invisible until the
        // snippet's dead man's switch fired eight seconds later.
        endRestoringPaint();
      }

      // Save on the way OUT, not only on click. The first version wrote the record at the
      // end of each click, so the remembered position was wherever the shopper happened to
      // be when they last pressed the button. A shopper presses it, keeps scrolling for
      // three more screens, then opens a product: coming back landed them at the button,
      // far above the shoe they had actually clicked. pagehide is the last moment the real
      // position is still readable, and it fires for both a normal navigation and a
      // bfcache suspend.
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('pageshow', this.onPageShow);

      // Sample the shopper's position WHILE they scroll, not when they leave.
      //
      // Reading it at pagehide is accurate on desktop and is not on iOS Safari: by the time
      // the event runs the page can already be torn down enough that scrollY reads 0 and
      // getBoundingClientRect puts the first card at the top, so we recorded "top of the
      // collection" and faithfully restored them there. It only showed when Safari had
      // dropped the page, which is why it looked intermittent and why it depended on how
      // long they lingered on the product.
      addEventListener('scroll', this.onScroll, { passive: true });

      // And one exact sample at the last possible honest moment: the tap itself.
      //
      // Scroll sampling alone leaves a gap. A shopper who presses "load more" and then taps a
      // product without scrolling has never fired a scroll event, so there is nothing recorded
      // and saveState falls back to measuring at pagehide, which is the unreliable path on
      // iOS. Capture phase, so it runs before any app's own click handling, and synchronous,
      // so the page is still fully laid out when it reads.
      const c = this.container;
      if (c) c.addEventListener('click', this.onCardClick, true);
    }

    disconnectedCallback() {
      // facets.js replaced the container: this instance is dead. Abandon in-flight work so
      // its response can never be appended into the new grid.
      if (this.controller) this.controller.abort();
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.reassertStop) this.reassertStop();
      removeEventListener('scroll', this.onScroll);
      const c = this.container;
      if (c) c.removeEventListener('click', this.onCardClick, true);
      if (this.anchorTimer) clearTimeout(this.anchorTimer);
      if (this.button) this.button.removeEventListener('click', this.onClick);
    }

    // ---------- element helpers ----------

    get container() {
      return this.closest('#ProductGridContainer');
    }

    /** The live <ul>. Re-queried every time; never cached across an await. */
    get list() {
      const c = this.container;
      return c ? c.querySelector('ul.product-grid') : null;
    }

    get nextUrl() {
      return (this.dataset.nextUrl || '').trim();
    }

    get exhausted() {
      const cur = parseInt(this.dataset.currentPage, 10) || 1;
      const tot = parseInt(this.dataset.totalPages, 10) || 1;
      return !this.nextUrl || cur >= tot;
    }

    togglePagination(hide) {
      const c = this.container;
      const nav = c ? c.querySelector('.pagination-wrapper') : null;
      if (nav) nav.hidden = !!hide;
    }

    label(key) {
      return (this.dataset['label' + key] || '').trim();
    }

    announce(msg) {
      // Looked up by id at announce time: the region lives OUTSIDE the container, so it
      // survives facet re-renders, but caching the node would still be wrong after one.
      const region = document.getElementById('LoadMoreStatus');
      if (region && msg) region.textContent = msg;
    }

    // ---------- fetching ----------

    /**
     * Fetch one page and append its cards.
     * @returns {Promise<number|null>} cards appended, or null when the attempt failed
     */
    async fetchPage(url) {
      this.controller = new AbortController();
      let doc;
      try {
        doc = await this.requestPage(url, this.controller.signal);
      } finally {
        this.controller = null;
      }
      if (doc === null) return null;
      return this.absorb(doc);
    }

    /**
     * One request, parsed, nothing touched. Split out from fetchPage so restoration can put
     * several of these in flight at once and absorb the results in page order afterwards.
     * @returns {Promise<Document|null>} null on any failure, including abort
     */
    async requestPage(url, signal) {
      try {
        const sep = url.includes('?') ? '&' : '?';
        const res = await nativeFetch(url + sep + 'section_id=' + this.dataset.sectionId, { signal });
        if (!res.ok) return null;
        return new DOMParser().parseFromString(await res.text(), 'text/html');
      } catch (_) {
        return null; // includes the abort that disconnectedCallback triggers
      }
    }

    /**
     * Append one already-fetched page's cards and adopt the server's pagination state.
     * @returns {Promise<number|null>} cards appended, or null if the grid went away
     */
    async absorb(doc, atOnce) {
      // Bail if the grid was swapped while we were waiting. Element identity IS the
      // generation token: a disconnected instance must never touch the new DOM.
      if (!this.isConnected) return null;

      const incoming = [...doc.querySelectorAll('#ProductGridContainer ul.product-grid > li')];

      // The template restarts forloop.index per request, so the first cards of every page
      // would render eager. Fix it while still detached, where images do not fetch.
      incoming.forEach((li) => {
        li.querySelectorAll('img:not([loading])').forEach((img) => img.setAttribute('loading', 'lazy'));
      });

      const live = this.list;
      if (!live) return null;

      // Offset pagination over a live collection can repeat a product when stock changes
      // between requests. Cards without a handle (search article/page results) always pass.
      const seen = new Set(
        [...live.querySelectorAll('product-card[data-handle]')].map((c) => c.dataset.handle)
      );
      const fresh = incoming.filter((li) => {
        const card = li.querySelector('product-card[data-handle]');
        if (!card) return true;
        if (seen.has(card.dataset.handle)) return false;
        seen.add(card.dataset.handle);
        return true;
      });

      if (atOnce) {
        // Restoration only. Batching exists to keep a TAP responsive while cards land, but
        // during a rebuild there is no tap to protect: the grid is hidden and the shopper is
        // waiting. Yielding between batches there only stretches the wait and, when the grid
        // was visible, made the cards arrive in visible waves.
        this.list.append(...fresh);
      } else {
        // Yielded batches: ~36 custom-element upgrades in one go blocks the tap.
        for (let i = 0; i < fresh.length; i += INSERT_BATCH) {
          if (!this.isConnected) return null;
          this.list.append(...fresh.slice(i, i + INSERT_BATCH));
          await raf();
        }
      }

      // Adopt the server's own view of where we are. Never inferred, never scraped from the
      // anchors, whose next/prev classes are inverted in this theme.
      const src = doc.querySelector('load-more');
      if (src) {
        this.dataset.nextUrl = src.dataset.nextUrl || '';
        this.dataset.currentPage = src.dataset.currentPage || this.dataset.currentPage;
        this.dataset.totalPages = src.dataset.totalPages || this.dataset.totalPages;
      }
      const srcNav = doc.querySelector('#ProductGridContainer .pagination-wrapper');
      const liveNav = this.container && this.container.querySelector('.pagination-wrapper');
      if (srcNav && liveNav) liveNav.innerHTML = srcNav.innerHTML;

      if (fresh.length) {
        document.dispatchEvent(
          new CustomEvent('weshoes:grid:appended', { detail: { items: fresh } })
        );
      }
      return fresh.length;
    }

    // ---------- interaction ----------

    onClick = async () => {
      try {
        await this.runClick();
      } catch (_) {
        // Last resort, and it exists because of where this runs: a live storefront where the
        // pagination is HIDDEN while this component is in charge. fetchPage already converts
        // network and parse failures into a null return, so nothing routine arrives here. An
        // unforeseen throw would otherwise leave the shopper with a spinning button, no
        // pagination and no way to see page 2 at all. fail() puts the real pagination back.
        this.fail();
      }
    };

    runClick = async () => {
      if (this.busy || this.exhausted) return;
      this.busy = true;
      this.button.setAttribute('aria-busy', 'true');
      const restore = this.button.textContent;
      this.button.textContent = this.label('Loading') || restore;

      let added = 0;
      let chained = 0;

      // A page that renders zero cards is NOT the end: {% if product.available %} runs
      // after paginate has already sliced. Chain past empties instead of hiding.
      while (chained < EMPTY_PAGE_CHAIN) {
        const url = this.nextUrl;
        if (!url) break;
        const n = await this.fetchPage(url);
        if (n === null) {
          this.fail();
          return;
        }
        this.pagesLoaded += 1;
        added += n;
        chained += 1;
        if (n > 0 || this.exhausted) break;
      }

      if (!this.isConnected) return;

      // Deliberately NOT setting history.scrollRestoration = 'manual' here.
      //
      // It was set, on the theory that the browser restoring scroll before the appended
      // cards exist would clamp to the bottom of a short page 1. The theory was right and
      // the decision was still wrong, because 'manual' sticks to the history entry: when
      // our own restore then failed for any reason, nobody scrolled at all and the shopper
      // landed at the top of the collection. That is worse than the flash we were avoiding,
      // and it is what shipped.
      //
      // Left on 'auto' the two mechanisms stack in the right order instead of competing.
      // The browser restores early from its remembered offset; our anchor scroll runs after
      // the refetches and overwrites it when it succeeds. On a bfcache hit the browser gets
      // it exactly right on its own and we do nothing at all.
      this.busy = false;
      this.button.removeAttribute('aria-busy');
      this.button.textContent = restore;

      if (this.exhausted) {
        this.button.disabled = true;
        this.button.textContent = this.label('Exhausted') || restore;
        this.announce(this.label('Exhausted'));
      } else if (this.cardCount() >= MAX_CARDS) {
        // Hand back to real pagination rather than dead-ending. See MAX_CARDS.
        this.button.disabled = true;
        this.button.textContent = this.label('Capped') || restore;
        this.togglePagination(false);
        this.announce(this.label('Capped'));
      } else {
        this.announce((this.label('Added') || '').replace('{count}', String(added)));
      }

      this.updateProgress();
      this.saveState();
    };

    fail() {
      // An aborted fetch reaches here too, and abort's usual cause is facets.js having just
      // replaced the grid. By then this instance is detached and this.button is the button of
      // a <load-more> that is no longer in the document, so every line below would throw on
      // null and take the rest of onClick with it. One guard, four null risks closed.
      if (!this.isConnected || !this.button) return;
      this.busy = false;
      this.button.removeAttribute('aria-busy');
      this.button.disabled = true;
      this.hidden = true;
      this.togglePagination(false); // the shopper can always page manually
      this.announce(this.label('Error'));
    }

    // ---------- state ----------

    saveState() {
      const live = this.list;
      if (!live) return;

      // Never record a document that was served deep. Restoration replays onto page 1 only,
      // and `page` is not part of the key, so a record written here would be read back by a
      // page-1 visit and rebuild it to an anchor and a scrollY belonging to a different
      // slice of the collection entirely.
      if (this.servedPage !== 1) {
        clearRecord(this.key);
        return;
      }

      // Nothing appended means there is nothing to rebuild, so CLEAR rather than return.
      //
      // Returning here was wrong in a way that only shows up on the second trip. A restore
      // that already found the anchor on page 1 appends nothing and leaves pagesLoaded at 1,
      // so an early return left the previous record in place: a stale anchor from an older
      // visit that then outranked the browser's own correct restoration on the next
      // back-navigation, permanently, until the tab was closed.
      if (this.pagesLoaded <= 1) {
        clearRecord(this.key);
        return;
      }

      writeRecord(this.key, {
        pages: this.pagesLoaded,
        liCount: live.querySelectorAll(':scope > li').length,
        cardCount: live.querySelectorAll('product-card[data-handle]').length,
        // The page size this grid actually uses. Collections read a section setting (36 at
        // the time of writing) and search hard-codes 24, so restoration cannot convert
        // between pages and cards using a constant. It reads this instead.
        pageSize: Math.max(1, Math.round(live.querySelectorAll(':scope > li').length / this.pagesLoaded)),
        // Sampled while scrolling where possible; see onScroll. Recomputing here would
        // reintroduce the iOS bug this exists to avoid.
        anchorHandle: this.lastAnchor || this.anchorInView(live),
        // Fallback for when the anchor product cannot be found on the way back: sold out
        // between visits, filtered away, or simply never refetched because the hard cap cut
        // the rebuild short. Landing near the right height beats landing at the top.
        scrollY: this.lastScrollY || Math.round(window.scrollY || window.pageYOffset || 0),
      });
    }

    /** The handle of the product nearest the top of the viewport, as a scroll anchor. */
    anchorInView(live) {
      const cards = [...live.querySelectorAll('product-card[data-handle]')];
      let best = null;
      let bestDist = Infinity;
      for (const c of cards) {
        const d = Math.abs(c.getBoundingClientRect().top);
        if (d < bestDist) {
          bestDist = d;
          best = c.dataset.handle;
        }
      }
      return best;
    }

    /**
     * Put the shopper at the top of the results after they filter or sort.
     *
     * Reported, and it is the right call: with three pages loaded the shopper is a few
     * thousand pixels down, and filtering leaves 36 products behind. The old offset then
     * clamps to the new maximum, which is the footer, and it reads as though nothing
     * happened. Every large shop scrolls back to the results here. facets.js does not: it
     * swaps the container's innerHTML and never touches the scroll.
     *
     * The feature exposed this rather than caused it, but it barely existed before, because
     * without a load-more button there was never much page to be scrolled past.
     *
     * Only ever scrolls UP. A shopper who filters from the top of the page must not be
     * pushed down to the grid, and the results heading and the filter controls sit above it.
     */
    scrollToResults() {
      const c = this.container;
      if (!c) return;
      const top = c.getBoundingClientRect().top + (window.scrollY || 0);
      // Enough room above the grid for the filter controls and the result count, so the
      // shopper can see that their filter took effect and can adjust it without scrolling up.
      const target = Math.max(0, Math.round(top - 100));
      if ((window.scrollY || 0) > target) window.scrollTo(0, target);
    }

    cardCount() {
      const live = this.list;
      return live ? live.querySelectorAll('product-card[data-handle]').length : 0;
    }

    /**
     * "showing 108 of 440" plus a bar.
     *
     * The denominator comes from the server (paginate.items) rather than from anything we can
     * count, because we only ever see what has been loaded. It also follows the active filter,
     * so the bar does not claim 440 while the shopper is looking at a filtered 37.
     *
     * The numerator counts <li>, not product-card, so search grids that mix in articles and
     * pages are still counted honestly against the same total the server paginated.
     */
    updateProgress() {
      if (!this.progress || !this.totalItems) return;
      const live = this.list;
      if (!live) return;
      const shown = Math.min(live.querySelectorAll(':scope > li').length, this.totalItems);
      const tpl = this.label('Progress');
      if (tpl && this.progressCount) {
        this.progressCount.textContent = tpl
          .replace('{shown}', String(shown))
          .replace('{total}', String(this.totalItems));
      }
      if (this.progressFill) {
        this.progressFill.style.inlineSize = Math.round((shown / this.totalItems) * 100) + '%';
      }
      this.progress.hidden = false;
    }

    findCard(handle) {
      const live = this.list;
      if (!handle || !live) return null;
      return live.querySelector(`product-card[data-handle="${CSS.escape(handle)}"]`);
    }

    /** Anchor if we can find it, saved height if we cannot, nothing if we have neither. */
    restoreScroll(rec) {
      const card = this.findCard(rec.anchorHandle);
      if (card) {
        // Two-argument scrollTo rather than scrollIntoView({behavior:'instant'}). 'instant'
        // is an enum member, so a browser that predates it throws a TypeError instead of
        // ignoring it, and that would take the whole restore down on exactly the old phones
        // least able to spare a wasted round trip.
        const box = card.getBoundingClientRect();
        const y = box.top + window.scrollY - Math.max(0, (window.innerHeight - box.height) / 2);
        window.scrollTo(0, Math.max(0, Math.round(y)));
        return;
      }
      if (typeof rec.scrollY === 'number' && rec.scrollY > 0) window.scrollTo(0, rec.scrollY);
    }

    // ---------- restoration ----------

    /**
     * Cheap on every frame, expensive only when the shopper stops.
     *
     * scrollY costs nothing, so it is taken on every tick. The anchor costs a
     * getBoundingClientRect per card, which with hundreds of cards is far too much to do
     * while a finger is moving, so it waits until the scrolling settles. That is also the
     * only moment it matters: a shopper taps a product after they have stopped to look at it.
     */
    onScroll = () => {
      this.lastScrollY = Math.round(window.scrollY || window.pageYOffset || 0);
      if (this.anchorTimer) clearTimeout(this.anchorTimer);
      this.anchorTimer = setTimeout(() => {
        const live = this.list;
        if (!live || !this.isConnected) return;
        this.lastAnchor = this.anchorInView(live);
      }, 150);
    };

    // Neither sampler gates on pagesLoaded any more. They used to, to save work on a page
    // where nothing had been appended, and that coupled the accuracy of the saved position to
    // a counter that can be wrong. Sampling costs a scrollY read per tick and one pass over
    // the cards when scrolling settles, which is not worth protecting against.
    onCardClick = (e) => {
      const link = e.target && e.target.closest && e.target.closest('a[href*="/products/"]');
      if (!link) return;
      const live = this.list;
      if (!live) return;
      this.lastScrollY = Math.round(window.scrollY || window.pageYOffset || 0);
      // The card they actually tapped beats the one nearest the viewport top: it is where
      // they are looking, and on the way back it is what they expect to be facing.
      const card = link.closest('li') && link.closest('li').querySelector('product-card[data-handle]');
      this.lastAnchor = (card && card.dataset.handle) || this.anchorInView(live);
    };

    /**
     * Win a race we did not start.
     *
     * Measured on an iPhone, twice, with the trace to prove it. Coming back from a product
     * page gives pageshow with persisted true and the document fully intact, and then:
     *
     *   ok:   0 -> 23 -> 2972          the offset is restored and it stays
     *   bad:  0 -> 23 -> 4720 -> 23    the offset is restored and 17ms later it is gone
     *
     * The 23 is present either way, so it is part of the platform's own resume sequence
     * rather than a bug. What differs is only the ORDER: sometimes it lands after the
     * restore instead of before it, and then the shopper is at the top of the collection.
     * That is why this looked intermittent for two days.
     *
     * It is not any script we can name. window.scrollTo, scrollIntoView,
     * scrollIntoViewIfNeeded, window.scroll, window.scrollBy, the element-level versions,
     * Element.focus and location.hash were all instrumented and all stayed silent through
     * both traces, so nothing is calling a scroll API.
     *
     * So this stops trying to identify it. A bfcache resume keeps the whole JavaScript
     * context, which means the position sampled while the shopper was scrolling is still in
     * memory and needs no storage, no rebuild and no network. Re-assert it for a few frames
     * until it holds, and whoever is competing loses because we go last.
     *
     * Yields to the shopper immediately. Any touch, wheel or key press ends it, because
     * fighting a finger would be far worse than the bug.
     */
    onPageShow = (e) => {
      if (!e.persisted) return;
      const want = this.lastScrollY || 0;
      if (want < 60) return; // nothing worth defending

      if (this.reassertStop) this.reassertStop();
      let frames = 0;
      let cancelled = false;
      const give = () => {
        cancelled = true;
        ['touchstart', 'wheel', 'keydown', 'pointerdown'].forEach((ev) =>
          removeEventListener(ev, give, { passive: true })
        );
        this.reassertStop = null;
      };
      ['touchstart', 'wheel', 'keydown', 'pointerdown'].forEach((ev) =>
        addEventListener(ev, give, { passive: true, once: true })
      );
      this.reassertStop = give;

      const tick = () => {
        if (cancelled || !this.isConnected) return;
        // A tolerance, not an equality test. The platform lands a pixel or two off, and
        // re-asserting over a position that is already right would be a visible twitch.
        if (Math.abs((window.scrollY || 0) - want) > 40) window.scrollTo(0, want);
        // About ten frames. Long enough to outlast a 17ms theft, short enough that a
        // shopper who starts reading is never overruled.
        if (++frames < 10) requestAnimationFrame(tick);
        else give();
      };
      requestAnimationFrame(tick);
    };

    onPageHide = () => {
      this.saveState();
    };

    async maybeRestore() {
      const rec = readRecord(this.key);
      if (!rec || rec.pages <= 1) return;

      // Only ever rebuild onto a fresh page 1. The li-count test is the idempotency guard:
      // it kills the duplicated-tab case and a second run over an already-rebuilt grid.
      if (this.servedPage !== 1) return;
      const live = this.list;
      if (!live) return;
      if (live.querySelectorAll(':scope > li').length >= (rec.liCount || 0)) return;

      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      const restore = this.button.textContent;
      const loading = this.label('Loading');
      if (loading) this.button.textContent = loading;

      // The scroll is left to the browser on purpose; see the note in the snippet. Whatever
      // it does during the rebuild happens behind a hidden grid, and we scroll to the anchor
      // before revealing, so there is nothing to take and nothing for anyone to see.
      try {
        // Both ceilings in the same unit. RESTORE_HARD_CAP used to be a page count while
        // MAX_CARDS is a card count, and they disagree whenever the page size is not the
        // collection's 36: search paginates by 24, so twenty pages is 480 cards and the loop
        // stopped short of an anchor well inside the card ceiling. rec.pageSize is measured.
        const pageSize = rec.pageSize || 36;
        const roomInCards = Math.max(0, MAX_CARDS - this.cardCount());
        const want = Math.min(rec.pages - 1, RESTORE_HARD_CAP, Math.floor(roomInCards / pageSize));

        // ALL AT ONCE, not one after the other. Measured on this store, restoring seven pages
        // sequentially took 11,540ms; the same seven in parallel took 2,248ms. The shopper
        // reported the sequential version as "really bad and slow" and they were right: it is
        // one round trip of latency multiplied by the number of pages, for pages the server
        // renders independently anyway.
        //
        // The reason this was sequential no longer exists. It was rejected as a burst of
        // thousands of event listeners, and that was true when every <quick-buy> registered
        // its own document listener. One listener now serves the whole document, and the
        // reflow burst is separately handled: absorb() still inserts in yielded batches, and
        // it is called in a loop below, so the DOM work stays spread across frames even
        // though the network work does not.
        //
        // Capped concurrency rather than a free-for-all. Every one of these is a full section
        // render on Shopify's side, and a shopper restoring twenty pages should not open
        // twenty simultaneous renders. Eight covers the overwhelming majority of real
        // journeys in a single wave.
        const urls = [];
        for (let p = 2; p <= want + 1; p++) {
          const u = new URL(location.href);
          u.searchParams.set('page', String(p));
          urls.push(u.pathname + u.search);
        }

        this.controller = new AbortController();
        const signal = this.controller.signal;
        const docs = new Array(urls.length).fill(undefined);
        let next = 0;
        const worker = async () => {
          while (next < urls.length) {
            const i = next++;
            docs[i] = await this.requestPage(urls[i], signal);
          }
        };
        await Promise.all(
          Array.from({ length: Math.min(RESTORE_CONCURRENCY, urls.length) }, worker)
        );
        this.controller = null;

        if (!this.isConnected) return;

        // Absorb in page order, and stop at the first gap. A hole in the middle would make
        // the grid lie about what comes next, and data-next-url is adopted from whatever we
        // absorbed last, so stopping at the gap keeps the fallback pagination truthful.
        for (const doc of docs) {
          if (!doc) break;
          if (this.cardCount() >= MAX_CARDS) break;
          const n = await this.absorb(doc, true); // at once: the grid is hidden, nothing to protect
          if (n === null) return; // grid swapped underneath us
          this.pagesLoaded += 1;
        }
      } finally {
        this.controller = null;
        // Unconditional. Whatever happened above, partial rebuild, aborted rebuild or an
        // exception, the shopper gets put as close to where they were as the DOM allows.
        if (this.isConnected) {
          await raf();
          this.restoreScroll(rec);
        }
      }

      if (!this.isConnected || !this.button) return;
      this.button.removeAttribute('aria-busy');
      const capped = !this.exhausted && this.cardCount() >= MAX_CARDS;
      this.button.disabled = this.exhausted || capped;
      if (this.exhausted) this.button.textContent = this.label('Exhausted') || restore;
      else if (capped) this.button.textContent = this.label('Capped') || restore;
      else this.button.textContent = restore;
      if (capped) this.togglePagination(false);

      // Scrolling already happened, in the finally block above, before this ran. It belongs
      // there rather than here so that a partial or aborted rebuild scrolls too.
      this.updateProgress();
      this.saveState();
    }
  }

  if (!customElements.get('load-more')) customElements.define('load-more', LoadMore);
})();
